import React, { useEffect, useState } from 'react';
import { Box, Card, Grid, Typography, Button, CircularProgress } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxIcon from '@mui/icons-material/Inbox'; // empty state icon
import Platform from '../Platform/Platform';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';



const API_URL = process.env.REACT_APP_API_URL;

function AcceptedRequests({ userId, searchResults, onLogout }) {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);  // Start with page 1
  const [allFetched, setAllFetched] = useState(false);
  const navigate = useNavigate();

  // Fetch data from the backend API
  const fetchBookings = async (page) => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/list_my_accepted_requests/?userId=${userId}&page=${page}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();

      // If no data is returned, we stop fetching
      if (data.length === 0) {
        setAllFetched(true);  // Mark as all data fetched
      } else {
        setCourses((prevCourses) => {
          // Avoid adding duplicate courses
          const newCourses = data.filter(course => !prevCourses.some(existingCourse => existingCourse.id === course.id));
          return [...prevCourses, ...newCourses];  // Append new data
        });
      }

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  // Fetch data when the page changes (triggered by scroll)
  useEffect(() => {
    if (userId && !allFetched) {
      fetchBookings(page);
    }
  }, [page, userId]);

  // Handle scroll to trigger next page fetch
  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if (windowHeight + scrollPosition >= documentHeight - 1 && !loading && !allFetched) {
      setPage((prevPage) => prevPage + 1);  // Increment page to load the next set of data
    }
  };

  // Add event listener for scroll on component mount and cleanup on unmount
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loading, allFetched]); // Only re-run the effect when `loading` or `allFetched` changes

  // UseEffect for handling `searchResults` updates
  useEffect(() => {
    if (searchResults) {
      setCourses(searchResults);  // Reset courses to searchResults if provided
      setPage(1);  // Reset page to 1 on new search
      setAllFetched(false);  // Allow further fetches if new search is performed
    }
  }, [searchResults]);

  // Format start time
  const formatStartTime = (time) => {
    const [hours, minutes] = time.split(':');
    const dateObj = new Date();
    dateObj.setHours(hours);
    dateObj.setMinutes(minutes);

    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });

    return formatter.format(dateObj);
  };

  // Format duration
  const formatDuration = (durationInMinutes) => {
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  if (!userId) {
    return <div>Loading</div>;; // Your own spinner or fallback
  }

  // Loading and error states
  if (loading && page === 1) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '3rem',
          color: 'text.secondary',
        }}
      >
        <HourglassEmptyIcon sx={{ fontSize: 40, mb: 2 }} />
        <Typography variant="h6" sx={{ letterSpacing: 2 }}>
          Loading your accepted requests...
        </Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '3rem',
          color: 'error.main',
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 40, mb: 2 }} />
        <Typography variant="h6" sx={{ letterSpacing: 2 }}>
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (courses.length === 0) {
    return (
      <div>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '3rem',
            color: 'text.secondary',

          }}
        >
          <Box
            sx={{
              position: 'fixed', // or 'relative' / 'sticky' based on your layout
              top: 0,
              left: 0,
              width: '100%',
              zIndex: 1300, // higher than MUI drawers (default is 1200)
            }}
          >
            <Header1 userId={userId} onLogout={onLogout} />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            align="center"
            fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
            sx={{
              backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
              mt: 10,
              letterSpacing: 7,
              WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
              color: 'transparent',  // Makes the text itself transparent to show the gradient
              // fontWeight: 'bold',  // Optional: you can make the text bold
            }}
            gutterBottom
          >
            Accepted Sessions
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '3rem',
              color: 'text.secondary',
              minHeight: '60vh',
            }}
          >
            <InboxIcon sx={{ fontSize: 50, mb: 2 }} />
            <Typography variant="h6" sx={{ letterSpacing: 2, textAlign: 'center' }}>
              Hey, you haven't accepted any requests yet!
            </Typography>
            <Box>
              <Platform userId={userId} />
            </Box>
            <Typography
              variant="body1"
              sx={{ maxWidth: 400, textAlign: 'center', mt: 1, fontSize: '1rem', letterSpacing: 2, }}
            >
              Check out the recommended request sessions and accept one based on your knowledge and interests.
            </Typography>
          </Box>
        </Box>
        <Footer2 />
      </div>
    );
  }


  const handleRedirect = () => {
    navigate('/meeting-guidelines');  // Redirect to meeting guidelines page
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  return (
    <div>
      <Box sx={{ padding: '2rem', width: '100%' }}>
        <Box
          sx={{
            position: 'fixed', // or 'relative' / 'sticky' based on your layout
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 1300, // higher than MUI drawers (default is 1200)
          }}
        >
          <Header1 userId={userId} onLogout={onLogout} />
        </Box>



        <Typography
          variant="h3"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            mt: 10,
            letterSpacing: 7,
            WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
            color: 'transparent',  // Makes the text itself transparent to show the gradient
            // fontWeight: 'bold',  // Optional: you can make the text bold
          }}
          gutterBottom
        >
          Accepted Sessions
        </Typography>


        <Box sx={{ display: 'flex', flexDirection: 'column', }}>
          <Box>
            <Platform userId={userId} />
          </Box>
          <Box>
            <Grid container spacing={4} direction="row" justifyContent="center">
              {courses.map((course) => {
                const fullDateTimeString = `${course.selectedDate}T${course.startTime}`;
                const sessionStartTime = new Date(fullDateTimeString);
                const sessionEndTime = new Date(course.session_end_time);
                const currentTime = new Date();
                const isSessionEnded = currentTime.getTime() >= sessionEndTime.getTime();
                const isSessionAboutToStart = currentTime.getTime() >= sessionStartTime.getTime() - 10 * 60 * 1000; // 10 minutes before start
                const isSessionOngoing = currentTime.getTime() >= sessionStartTime.getTime() && currentTime.getTime() <= sessionEndTime.getTime();

                return (
                  <Grid item xs={12} key={course.id}>
                    <Link
                      to={`/accepted-request-tab-details/${course.id}/${userId}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          p: 2,
                          backgroundColor: "#000000",
                          color: "#e0e0e0",
                          borderBottom: "1px solid #e0e0e0",
                          borderRadius: 0,
                          transition: "background-color 0.2s ease-in-out",
                          cursor: "pointer",
                          '&:hover': {
                            backgroundColor: "#2222",
                          },
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {/* Topic Name */}
                        <Box sx={{ flex: 3, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 400,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: { xs: "1rem", sm: "1.2rem" },
                              '&:hover': {
                                color: "#1E90FF",
                              },
                            }}
                          >
                            {course.description.length > 20
                              ? `${course.description.substring(0, 20)}...`
                              : course.description}
                          </Typography>
                        </Box>

                        {/* Date */}
                        <Box sx={{ flex: 1, minWidth: 100 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.9rem" },
                              whiteSpace: "nowrap",
                              letterSpacing: 1,
                            }}
                          >
                            📅 Date:  {formatDate(course.selectedDate)}
                          </Typography>
                        </Box>

                        {/* Time */}
                        <Box sx={{ flex: 1, minWidth: 100 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.9rem" },
                              whiteSpace: "nowrap",
                              letterSpacing: 1,
                            }}
                          >
                            ⏰ Time: {formatStartTime(course.startTime)}
                          </Typography>
                        </Box>

                        {/* Button */}
                        <Box sx={{ flexShrink: 0, mt: { xs: 1, sm: 0 }, width: { xs: "100%", sm: "auto" } }}>
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{
                              fontSize: "0.85rem",
                              px: 2,
                              py: 1,
                              textTransform: "none",
                              borderRadius: "6px",
                              backgroundImage: "linear-gradient(45deg, #2196F3, #1976D2)",
                              '&:hover': {
                                backgroundImage: "linear-gradient(45deg, #1976D2, #2196F3)",
                                transform: "scale(1.03)",
                              },
                              width: "100%",
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Link>
                  </Grid>

                );
              })}
            </Grid>
          </Box>
        </Box>
      </Box>
      <Footer2 />
    </div>
  );
}

export default AcceptedRequests;
